import { AllProspectsDataExportDto, Prospect } from '../api/Prospect';
import * as moment from 'moment';
import { formatPhoneNumber } from './Utils';

// https://stackoverflow.com/questions/14964035/how-to-export-javascript-array-info-to-csv-on-client-side
// The download function takes a CSV string, the filename and mimeType as parameters
export const downloadCsv = (content: string, fileName: string, mimeType: string) => {
    var a = document.createElement('a');
    mimeType = mimeType || 'application/octet-stream';

    if (navigator.msSaveBlob) {
        // IE10
        navigator.msSaveBlob(
            new Blob([content], {
                type: mimeType
            }),
            fileName
        );
    } else if (URL && 'download' in a) {
        // html5 A[download]
        a.href = URL.createObjectURL(
            new Blob([content], {
                type: mimeType
            })
        );
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } else {
        location.href = 'data:application/octet-stream,' + encodeURIComponent(content);
        // only this mime type is supported
    }
};

export const exportLog = <T>(fileName: string, csvContent: string) => {
    downloadCsv(csvContent, `${fileName}.csv`, 'text/csv;encoding:utf-8');
};

export function exportActivityData(
    prospects: Prospect[],
    includeTourData: boolean,
    startDate: moment.Moment,
    endDate: moment.Moment,
    includeProperty: boolean
) {
    let csvString =
        `${includeProperty ? 'Property,' : ''
        }First Name,Last Name,Date Created,Email,Phone,Move In Date,Apartment Type Description,` +
        'Contact Preference,Pet,Desired Lease Length,Referral Source,' +
        `Number of Additional Residents,CC Status,ID Status,Partner${includeTourData ? ',Tour Start,Tour End,Tour Type,Status' : ''
        }`;

    prospects.forEach(p => {
        let contactPreference = '';

        switch (p.contactInfo.contactPreference) {
            case 1:
                contactPreference = 'Phone/Text';
                break;
            case 2:
                contactPreference = 'Email';
                break;
            case 3:
                contactPreference = 'Email & Phone/Text';
                break;
            default:
                break;
        }

        csvString += '\n';
        if (includeProperty) {
            csvString += `"=""${p.propertyName}""",`;
        }
        csvString += `"=""${p.contactInfo.firstName}"""`;
        csvString += `,"=""${p.contactInfo.lastName}"""`;
        csvString += `,"=""${p.contactInfo.dateCreated
            ? moment(p.contactInfo.dateCreated).format('M/DD/YYYY h:mm A')
            : ''
            }"""`;
        csvString += `,"=""${p.contactInfo.email}"""`;
        csvString += `,"=""${formatPhoneNumber(p.contactInfo.phone)}"""`;
        csvString += `,"=""${p.contactInfo.moveInDate ? moment(p.contactInfo.moveInDate).format('M/DD/YYYY') : ''
            }"""`;
        csvString += `,"=""${p.contactInfo.apartmentTypeDescription}"""`;
        csvString += `,"=""${contactPreference}"""`;
        csvString += p.contactInfo.pet === null ? `,"=""N/A"""` : `,"=""${p.contactInfo.pet}"""`;

        csvString +=
            p.contactInfo.desiredLeaseLength === null
                ? `,"=""N/A"""`
                : `,"=""${p.contactInfo.desiredLeaseLength} Months"""`;
        csvString += `,"=""${p.contactInfo.source}"""`;
        csvString += `,"=""${p.contactInfo.additionalResidents.length}"""`;
        csvString += `,"=""${p.creditCardStatus}"""`;
        csvString += `,"=""${p.idCheckStatus}"""`;
        csvString += `,"=""${p.contactInfo.partnerName}"""`;

        if (includeTourData) {
            csvString += `,"=""${p.tourInfo.event
                ? moment(p.tourInfo.event.startDateTime).format('M/DD/YYYY h:mm A')
                : ''
                }"""`;
            csvString += `,"=""${p.tourInfo.event
                ? moment(p.tourInfo.event.endDateTime).format('M/DD/YYYY h:mm A')
                : ''
                }"""`;

            csvString += `,"=""${p.tourInfo.tourTypeDisplay}"""`;
            csvString += `,"=""${p.tourInfo.status}"""`;
        }
    });

    let dateRange = `${startDate.format('M-DD-YYYY')} through ${endDate.format('M-DD-YYYY')}`;

    exportLog(
        includeTourData
            ? `tour24 Tours ${dateRange}`
            : `tour24 Prospects without Tours ${dateRange}`,
        csvString
    );
}

export function exportAllProspectsData(data: AllProspectsDataExportDto[], fileName: string) {
    let csvString =
        'Property,Company,First Name,Last Name,Date Created,Time Created,Email,' +
        'Phone,Move In Date,Apartment Type Description,Contact Preference,' +
        'Pet,Desired Lease Length,Source,' +
        'Number of Additional Residents,CC Qualified,ID Verified,' +
        'Tour Date,Day Of Week,Tour Start,Tour End,Tour Type,Status,Lease Applied';

    data.forEach(d => {
        csvString += '\n';
        csvString += `"${d.propertyName}"`;
        csvString += `,"${d.companyName}"`;
        csvString += `,"${d.firstName}"`;
        csvString += `,"${d.lastName}"`;
        csvString += `,"${moment(d.dateCreated).format('M/DD/YYYY')}"`;
        csvString += `,"${moment(d.dateCreated).format('HH:mm')}"`;
        csvString += `,"${d.email}"`;
        csvString += `,"${d.phone}"`;
        csvString += `,"${moment(d.moveInDate).format('M/DD/YYYY')}"`;
        csvString += `,"${d.apartmentTypeDescription}"`;
        csvString += `,"${d.contactPreference}"`;
        csvString += `,"${d.pet}"`;
        csvString += `,"${d.desiredLeaseLength}"`;
        csvString += `,"${d.source}"`;
        csvString += `,"${d.additionalResidents}"`;
        csvString += `,"${d.ccQualified}"`;
        csvString += `,"${d.idVerified}"`;
        if (!d.tour) {
            csvString += ',,,,,,,';
        } else {
            csvString += `,"${moment(d.tour.tourStart).format('M/DD/YYYY')}"`;
            csvString += `,"${d.tour.tourDay}"`;
            csvString += `,"${moment(d.tour.tourStart).format('HH:mm')}"`;
            csvString += `,"${moment(d.tour.tourEnd).format('HH:mm')}"`;
            csvString += `,"${d.tour.tourType}"`;
            csvString += `,"${d.tour.status}"`;
            csvString += `,"${d.tour.leaseApplied}"`;
        }
        //csvString += `,"${d.partnerName}"`;
    });

    exportLog(fileName, csvString);
}

export function exportAnalyticsData(data: any[]) {
    let csvString =
        'Property,Company,First Name,Last Name,Date Created,Time Created,Email,' +
        'Phone,Move In Date,Apartment Type Description,Contact Preference,' +
        'Pet,Desired Lease Length,Source,Number of Additional Residents,' +
        'CC Qualified,ID Verified,Tour Date,Day Of Week,Tour Start,' +
        'Tour End,Tour Type,Status,Application Status,Partner,Same Day,' +
        'Within 60 Mins,Outside 9-5 BH';

    data.forEach(d => {
        csvString += '\n';
        csvString += `"${d.property}"`;
        csvString += `,"${d.company}"`;
        csvString += `,"${d.firstName}"`;
        csvString += `,"${d.lastName}"`;
        csvString += `,"${moment(d.dateCreated, 'M/DD/YYYY').format('M/DD/YYYY')}"`;
        csvString += `,"${d.timeCreated? moment(d.timeCreated,'HH:mm').format('HH:mm'):"Invalid time"}"`;
        csvString += `,"${d.email}"`;
        csvString += `,"${d.phone}"`;
        csvString += `,"${moment(d.moveInDate, 'M/DD/YYYY').format('M/DD/YYYY')}"`;
        csvString += `,"${d.apartmentTypeDescription}"`;
        csvString += `,"${d.contactPreference}"`;
        csvString += `,"${d.pet}"`;
        csvString += `,"${d.desiredLeaseLength}"`;
        csvString += `,"${d.source}"`;
        csvString += `,"${d.numberOfAdditionalResidents}"`;
        csvString += `,"${d.ccQualified}"`;
        csvString += `,"${d.idVerified}"`;
        csvString += `,"${moment(d.tourDate, 'M/DD/YYYY').format('M/DD/YYYY')}"`;
        csvString += `,"${d.dayOfWeek}"`;
        csvString += `,"${d.tourStart? moment(d.tourStart,'HH:mm').format('HH:mm'):"Invalid time"}"`;
        csvString += `,"${d.tourEnd? moment(d.tourEnd,'HH:mm').format('HH:mm'):"Invalid time"}"`;
        csvString += `,"${d.tourType}"`;
        csvString += `,"${d.status}"`;
        csvString += `,"${d.applicationStatus}"`;
        csvString += `,"${d.partner}"`;
        csvString += `,"${d.sameDay}"`;
        csvString += `,"${d.within60Mins}"`;
        csvString += `,"${d.outside9to5BH}"`;
    });

    exportLog('tour24 Analytics Data Export', csvString);
}


// WEBPACK FOOTER //
// ./src/utilities/exportLog.ts